import {
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import type { CanvasEditor, CanvasEditorSnapshot } from "../controller/types";
import { referenceTargetKey } from "../domain/canvas";
import type { CanvasNode } from "../domain/types";
import {
  ReferenceResolutionCache,
  resolveCanvasReferences,
  type CanvasAssetResolver,
  type ReferenceResolution,
  type ReferenceResolver,
} from "../references";

export function useCanvasSnapshot(editor: CanvasEditor): CanvasEditorSnapshot {
  return useSyncExternalStore(
    editor.subscribe,
    editor.getSnapshot,
    editor.getSnapshot,
  );
}

export interface ReferenceResolutionState {
  resolutions: ReadonlyMap<string, ReferenceResolution>;
  pendingKeys: ReadonlySet<string>;
  resolverAvailable: boolean;
  error?: Error;
}

export function useReferenceResolutions(
  nodes: readonly CanvasNode[],
  resolver?: ReferenceResolver,
  refreshKey?: string | number,
): ReferenceResolutionState {
  const targetKey = useMemo(
    () =>
      nodes
        .filter(
          (node) => node.type === "reference" && node.reference !== undefined,
        )
        .map((node) => referenceTargetKey(node.reference!))
        .sort()
        .join("|"),
    [nodes],
  );
  const [state, setState] = useState<ReferenceResolutionState>({
    resolutions: new Map(),
    pendingKeys: new Set(),
    resolverAvailable: resolver !== undefined,
  });
  const [resolverRevision, setResolverRevision] = useState(0);
  const cache = useMemo(
    () => new ReferenceResolutionCache({ concurrency: 4, maxAgeMs: 30_000 }),
    [resolver],
  );
  const previousRefreshKey = useRef(refreshKey);
  const forceRefresh = previousRefreshKey.current !== refreshKey;
  previousRefreshKey.current = refreshKey;

  useEffect(() => {
    if (resolver?.subscribe === undefined) return;
    return resolver.subscribe((target) => {
      cache.invalidate(target);
      setResolverRevision((current) => current + 1);
    });
  }, [cache, resolver]);

  useEffect(() => {
    if (resolver === undefined) {
      setState({
        resolutions: new Map(),
        pendingKeys: new Set(),
        resolverAvailable: false,
      });
      return;
    }
    const controller = new AbortController();
    const targetNodes = nodes.filter(
      (node) => node.type === "reference" && node.reference !== undefined,
    );
    const pendingKeys = new Set(
      targetNodes.map((node) => referenceTargetKey(node.reference!)),
    );
    setState((current) => ({
      ...current,
      resolverAvailable: true,
      pendingKeys,
    }));
    void resolveCanvasReferences(targetNodes, resolver, controller.signal, {
      cache,
      concurrency: 4,
      force: forceRefresh,
    })
      .then((resolutions) => {
        if (!controller.signal.aborted) {
          setState({
            resolutions,
            pendingKeys: new Set(),
            resolverAvailable: true,
          });
        }
      })
      .catch((error: unknown) => {
        if (!controller.signal.aborted) {
          setState({
            resolutions: new Map(),
            pendingKeys: new Set(),
            resolverAvailable: true,
            error:
              error instanceof Error
                ? error
                : new Error("Reference resolution failed"),
          });
        }
      });
    return () => controller.abort();
  }, [cache, refreshKey, resolver, resolverRevision, targetKey]);

  return state;
}

export function useAssetUrl(
  attachmentId: string | undefined,
  resolver?: CanvasAssetResolver,
): string | null | undefined {
  const [url, setUrl] = useState<string | null>();
  useEffect(() => {
    if (attachmentId === undefined || resolver === undefined) {
      setUrl(undefined);
      return;
    }
    const controller = new AbortController();
    let release: (() => void) | undefined;
    let released = false;
    const releaseOnce = () => {
      if (released || release === undefined) return;
      released = true;
      try {
        release();
      } catch {
        // Resource cleanup must not break React unmounting.
      }
    };
    setUrl(undefined);
    void resolver
      .resolveUrl(attachmentId, controller.signal)
      .then((resolved) => {
        const nextUrl =
          typeof resolved === "object" && resolved !== null
            ? resolved.url
            : resolved;
        if (typeof resolved === "object" && resolved !== null) {
          // Preserve the lease object's receiver: hosts may implement release
          // as a method that reads instance state.
          release = () => resolved.release();
        }
        if (controller.signal.aborted) {
          releaseOnce();
          return;
        }
        setUrl(nextUrl);
      })
      .catch(() => {
        if (!controller.signal.aborted) setUrl(null);
      });
    return () => {
      controller.abort();
      releaseOnce();
    };
  }, [attachmentId, resolver]);
  return url;
}
